import React, { Component, type ComponentType } from 'react';
import { type Store } from 'redux';
import { Provider } from 'react-redux';
import { IntlProvider } from 'react-intl-next';
import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { withCrossFlow, type WithCrossFlowProps } from '@atlassiansox/cross-flow-component-support';
import OriginTracing from '@atlassiansox/origin-tracing';
import * as untypedI18n from './i18n';
import addSupportedLocaleData from './common/i18n/addSupportedLocaleData';

import { initAnalyticsClient } from './common/analytics/analytics-web-client';
import View from './view/connected';
import { type WithFireExposureEventProps } from './view/types';
import { type Locale } from './common/constants/supported-locales';
import { initialProjectState } from './state/project/reducer';
import { initialContextState } from './state/context/reducer';
import { type State } from './state/types';
import { createStore } from './state';
import { set as setExternal } from './state/external/actions';
import { set as setContext } from './state/context/actions';
import { set as setFeatureFlags } from './state/feature-flags/actions';
import { set as setProject } from './state/project/actions';
import { set as setActivation } from './state/activation/actions';
import { ProjectPagesProvider } from './containers/project-pages';
import { FeatureFlagsProvider } from './containers/feature-flags';
import { CONFLUENCE_LOADING } from './state/context/types';
import { PROJECT_PAGES_SOURCE_COMPONENT, PROJECT_PAGES_SOURCE_CONTEXT } from './common/constants';
import { ThemeContext, whiteTheme } from './ui/theme';
import { type Theme } from './ui/theme/types';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/analytics-bridge';
import { type OnGetFeatureFlagValueType } from './state/external/types';
import { JSW_PARENT_PRODUCT, type ParentProducts } from './common/constants/parent-products';
import { getAllBooleanFeatureFlags } from './common/util/get-boolean-feature-flags';
//REMOVE AFTER CBT ENGINEERS TEST BRANCH DEPLOY

type OwnProps = {
	baseUrl: string;
	external?: any;
	locale: Locale;
	isAdmin: boolean;
	cloudId: string;
	accountId: string;
	parentProduct?: ParentProducts;
	projectKey: string;
	projectId: string | number;
	projectName: string;
	projectType: string;
	environment?: string;
	EmbeddedProductUpdater: ComponentType<any>;
	breadcrumbs: React.ReactElement<any>;
	onGetFeatureFlagValue: OnGetFeatureFlagValueType;
	hasConfluence: boolean;
	renderFullHeader?: boolean;
	theme?: Theme;
	isProjectPagesSporkRebrandingEnabled?: boolean;
} & WithFireExposureEventProps;

type Props = WithCrossFlowProps & OwnProps;

const i18n: {
	[index: string]: Record<string, string> | undefined;
} = untypedI18n;

const getCodesFromLocale = (locale: string) => {
	const match = /([a-z]*)[_-]?([A-Z]*)/i.exec(locale || '');
	if (!match) {
		throw Error('Unable to get language and country from invalid Locale');
	}
	const [, language, country] = match;

	return {
		language: language.toLowerCase(),
		country: country.toUpperCase(),
	};
};

class ProjectPagesComponent extends Component<Props> {
	context: any;
	static defaultProps: Partial<Props> = {
		baseUrl: initialContextState.baseUrl,
		locale: initialContextState.locale,
		isAdmin: initialContextState.isAdmin,
		cloudId: initialContextState.cloudId,
		accountId: initialContextState.accountId,
		parentProduct: JSW_PARENT_PRODUCT,
		projectKey: initialProjectState.key,
		projectId: initialProjectState.id,
		projectType: initialProjectState.type,
		projectName: initialProjectState.name,
		environment: 'dev',
		hasConfluence: false,
	};

	constructor(props: Props) {
		super(props);
		const {
			baseUrl,
			locale,
			isAdmin,
			cloudId,
			accountId,
			projectKey,
			projectId,
			projectType,
			projectName,
			environment,
			onGetFeatureFlagValue,
			onFireExposureEvent,
			onFireErrorEvent,
			isProjectPagesSporkRebrandingEnabled,
			parentProduct,
		} = props;

		const origin = new OriginTracing({ product: 'jira' });
		this.store = createStore();
		this.store.dispatch(
			setContext({
				baseUrl: baseUrl as string,
				locale: locale as Locale,
				isAdmin: isAdmin as boolean,
				cloudId: cloudId as string,
				accountId: accountId as string,
				confluenceState: CONFLUENCE_LOADING,
				suggestedKey: initialContextState.suggestedKey,
				// origin: origin as OriginTracing,
				origin,
				environment: environment || 'dev',
				licenseInformation: undefined,
				projectId: projectId as number,
				projectType,
			}),
		);
		this.store.dispatch(
			setExternal({
				onGetFeatureFlagValue,
				onFireExposureEvent,
				onFireErrorEvent,
				crossFlow: props.crossFlow,
			}),
		);
		this.store.dispatch(
			setProject({
				key: projectKey as string,
				id: projectId as number,
				type: projectType as string,
				name: projectName,
			}),
		);

		this.store.dispatch(
			setFeatureFlags({
				isProjectPagesSporkRebrandingEnabled: isProjectPagesSporkRebrandingEnabled || false,
			}),
		);

		const isOnDemandEnabled =
			isAdmin &&
			parentProduct === JSW_PARENT_PRODUCT &&
			fg('platform.xflow-provisioning-api_kgxk3');

		this.store.dispatch(
			setActivation({
				status: isOnDemandEnabled ? 'not-started' : 'not-allowed',
				retryCount: 0,
				userInitiated: false,
			}),
		);

		initAnalyticsClient({
			client: {
				environment,
				locale,
				cloudId,
				accountId,
			},
			context: {
				containerType: 'project',
				containerId: projectId,
				parent: 'projectPages',
			},
		});
	}

	store: Store<State>;
	render() {
		const {
			accountId,
			locale,
			cloudId,
			breadcrumbs,
			EmbeddedProductUpdater,
			projectKey,
			projectName,
			projectId,
			projectType,
			parentProduct,
			hasConfluence,
			renderFullHeader,
			onFireExposureEvent,
			onFireErrorEvent,
			theme,
			isAdmin,
			environment,
		} = this.props;

		const { language, country } = getCodesFromLocale(locale as string);
		const messages = i18n[`${language}_${country}`] || i18n[language];
		const defaultParentProduct = parentProduct ?? JSW_PARENT_PRODUCT;

		addSupportedLocaleData();
		return (
			<Provider store={this.store}>
				<IntlProvider locale={language} messages={messages} defaultLocale="en">
					<ProjectPagesProvider
						value={{
							accountId,
							cloudId,
							locale,
							parentProduct: defaultParentProduct,
							projectKey,
							projectName,
							projectId,
							projectType,
							isAdmin,
							environment,
						}}
					>
						<FeatureFlagsProvider
							value={{
								fireFeatureExposed: onFireExposureEvent,
							}}
						>
							<FlagsProvider>
								<ThemeContext.Provider value={theme || whiteTheme}>
									<View
										breadcrumbs={breadcrumbs}
										locale={locale}
										EmbeddedProductUpdater={EmbeddedProductUpdater}
										hasConfluence={hasConfluence}
										renderFullHeader={renderFullHeader}
										onFireExposureEvent={onFireExposureEvent}
										onFireErrorEvent={onFireErrorEvent}
									/>
								</ThemeContext.Provider>
							</FlagsProvider>
						</FeatureFlagsProvider>
					</ProjectPagesProvider>
				</IntlProvider>
			</Provider>
		);
	}
}

const ProjectPagesWithCrossFlow = withCrossFlow(ProjectPagesComponent);

export default (props: OwnProps) => (
	<>
		<ContextualAnalyticsData
			sourceType={SCREEN}
			sourceName="projectPages"
			containerType="project"
			containerId={props.projectId ? `${props.projectId}` : undefined}
			attributes={{
				hasConfluence: props.hasConfluence,
				projectId: props.projectId,
				sourceComponent: PROJECT_PAGES_SOURCE_COMPONENT,
				sourceContext: PROJECT_PAGES_SOURCE_CONTEXT,
				isGoogleDriveInProjectPagesEnabled: fg('platform.google-drive-in-project-pages') || false,
				featureFlags: { ...getAllBooleanFeatureFlags() },
			}}
		>
			<ProjectPagesWithCrossFlow {...props} />
		</ContextualAnalyticsData>
	</>
);
